import { Text } from '@chakra-ui/react';

import { TrashAlt } from '#icons/trash-alt.tsx';

import type { DeleteDialogProps } from './types/delete-dialog.props';

import { ConfirmationDialog } from './confirmation-dialog';

/**
 * A generic dialog component for confirming deletion actions.
 *
 * Renders a modal dialog prompting the user to confirm deletion of a resource,
 * displaying a warning that the action cannot be undone. Accepts customizable
 * confirmation text, resource name, and trigger element. Handles loading state
 * and invokes the provided `onDelete` callback when confirmed.
 *
 * @template TData - The type of data associated with the deletion.
 * @param confirm - Whether confirmation is required before deletion.
 * @param confirmationText - Custom text for the confirmation input.
 * @param data - Data to be passed to the delete handler.
 * @param isLoading - Indicates if the deletion is in progress.
 * @param isOpen - Controls the visibility of the dialog.
 * @param onDelete - Callback invoked when deletion is confirmed.
 * @param resourceName - Name of the resource being deleted.
 * @param trigger - Element that triggers the dialog.
 */
export const DeleteDialog = <TData = unknown>({
  confirm = false,
  confirmationText = 'delete',
  data,
  defaultIsOpen,
  isLoading = false,
  isOpen = false,
  onCancel,
  onDelete,
  resourceName,
  trigger,
}: DeleteDialogProps<TData>) => {
  return (
    <ConfirmationDialog
      cancelLabel="Cancel"
      colorScheme="red"
      confirm={confirm}
      confirmationText={confirmationText}
      confirmLabel="Delete"
      defaultIsOpen={defaultIsOpen}
      description={
        <>
          <Text>Are you sure you want to delete {resourceName}?</Text>
          <Text mt={1}>This action cannot be undone.</Text>
        </>
      }
      icon={
        <TrashAlt
          size="2x"
          widthAuto
        />
      }
      isLoading={isLoading}
      isOpen={isOpen}
      onCancel={onCancel}
      onConfirm={(onClose) => {
        return onDelete?.(onClose, data);
      }}
      title="Confirm Deletion"
      trigger={trigger}
    />
  );
};
